/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { logDebug } from '@gh/core-util';
import { delay$, filter$, publish$ } from '@gh/rx/operators';
import { isNil, last } from 'lodash';
import { BehaviorSubject, ConnectableObservable, Observable, Subject, Subscription } from 'rxjs';
import { normalizeStep } from './action-util';
import {
  Action,
  ActionDispatcher,
  ActionExecutor,
  ActionFlow,
  ActionFlowSubscription,
  FlowState,
  Step,
} from './action.interfaces';
import { compoundConstantAction, STEP_COMPLETE, STEP_POP_CONTEXT, STEP_PUSH_CONTEXT, STEP_START } from './actions';

const log = (...args: any[]): void => log(args);

type ActionFlowContext<FS> = {
  step: Step;
  actions: Hash<ActionExecutor<FS>>;
};

export function createFlow<S>(actions: Hash<ActionExecutor<S>>): ActionFlow<S> {
  const action = <any>compoundConstantAction('*', actions);
  action.run = (state: any) => runFlow(state, action);
  return action;
}

function runFlow<FS>(initState: FS, rootAction: Action<FS>): ActionFlowSubscription<FS> {
  const contextStack: ActionFlowContext<FS>[] = [{
    step: normalizeStep(STEP_START),
    actions: { [STEP_START]: rootAction },
  }];

  const finalState$$ = new BehaviorSubject<Maybe<FS>>(void 0);
  const flow$$ = new BehaviorSubject<FlowState<FS>>({
    step: normalizeStep(STEP_START),
    state: initState,
  });

  let dumpOn = false;

  const dispatch: ActionDispatcher = (step, state) => {
    if (dumpOn) {
      // tslint:disable-next-line:max-line-length
      logDebug(`Action ${contextStack.slice(1).map((context) => context.step.type).join(' -> ')} -> ${normalizeStep(step).type}`);
    }

    flow$$.next({ step: normalizeStep(step), state });
  };

  const safeDispatch = (action: Action<FS>, state: FS, step: Step) => {
    try {
      action(dispatch, state, step);
    } catch (err) {
      flow$$.error(err);
    }
  };

  const flow$ = <ConnectableObservable<FlowState<FS>>>flow$$.pipe(publish$());

  // create new execution context
  flow$.pipe(filter$(byStepType(STEP_PUSH_CONTEXT))).subscribe((state) => {
    const { step } = state;
    const { actions } = step;

    contextStack.push({ step, actions });

    dispatch(STEP_START, state.state);
  });

  // remove execution context
  flow$.pipe(filter$(byStepType(STEP_POP_CONTEXT))).subscribe((state) => {
    const { nextStep } = state.step;

    contextStack.pop();

    if (contextStack.length === 1) {
      dispatch(STEP_COMPLETE, state.state);
    } else {
      if (isNil(nextStep)) {
        throw new Error(`Next step is undefined for '${STEP_POP_CONTEXT}' step`);
      } else {
        dispatch(normalizeStep(nextStep), state.state);
      }
    }
  });

  // complete execution flow
  flow$.pipe(filter$(byStepType(STEP_COMPLETE))).subscribe(() => {
    flow$$.complete();
    finalState$$.next(flow$$.value.state);
  });

  // run custom steps
  flow$.pipe(filter$(isCustomStep)).subscribe((flowState) => {
    const { state, step } = flowState;
    const nextAction = findActionByPattern(last(contextStack)!, step.type); // tslint:disable-line:no-non-null-assertion

    safeDispatch(nextAction, state, step);
  });

  flow$.subscribe({
    error: (error) => finalState$$.error(error),
  });

  flow$.connect();

  class ActionFlowSubscriptionImpl implements ActionFlowSubscription<FS> {
    watch(...args: any[]): Subscription {
      return flow$.subscribe.apply(flow$, args);
    }

    dump(): this {
      dumpOn = true;
      return this;
    }

    toObservable(): Observable<FlowState<FS>> {
      return flow$;
    }

    toFinalState(): Observable<FS> {
      // @ts-ignore
      return finalState$$.pipe(filter$(Boolean));
    }

    cancel(): void {
      flow$$.error(new Error('cancelled'));
    }
  }

  return new ActionFlowSubscriptionImpl();
}

function findActionByPattern<FS>(context: ActionFlowContext<FS>, pattern?: string): Action<FS> {
  if (isNil(pattern)) {
    throw new Error('Step is not defined');
  }

  let action = context.actions[pattern];

  if (isNil(action)) {
    action = context.actions['*'];
  }

  if (isNil(action)) {
    throw new Error(`Action cannot be found by pattern ${pattern}`);
  }

  return action;
}

function byStepType<FS>(type: string): PredicateFn<FlowState<FS>> {
  return (state) => state.step.type === type;
}

function isCustomStep<FS>(state: FlowState<FS>): boolean {
  return state.step.type.charAt(0) !== '$';
}
